import { useState, useEffect, useRef } from "react";
import { DS } from "../theme";
import { getUserPurchases, analyzeGame } from "../lib/api";
import { supabase } from "../lib/supabase";

const API_URL = process.env.REACT_APP_API_URL;
const SIDEBAR_W = 340;
const FREE_HINT_LIMIT = 3;

export default function Blanche({
  user,
  currentPgn,
  playerColor,
  onClose,
  isMobile,
  liveMessages = [],
  isBlancheGame = false,
  gameOver = false,
  onStartBlancheGame,
  commentaryOn = true,
  onToggleCommentary,
  onNavigate,
  onPlayOnline,
  onNewGame,
  autoHint = null,
  hintCount = 0,
  onHintUsed,
}) {
  const [view, setView]           = useState(gameOver && currentPgn ? "analysis" : "chat");
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError]         = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading]     = useState(true);
  const [analysis, setAnalysis]   = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const bottomRef = useRef(null);
  const didAutoHint = useRef(false);

  const hasAccess = subscription?.status === "active";
  const isPro     = hasAccess && subscription?.tier === "pro";
  const isFree    = !hasAccess;

  useEffect(() => {
    if (!user?.token) { setSubLoading(false); return; }
    getUserPurchases(user.token)
      .then(data => setSubscription(data?.subscription))
      .catch(() => {})
      .finally(() => setSubLoading(false));
  }, [user]);

  // Load chat history
  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API_URL}/blanche/history`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setMessages(data.map(m => ({ role: m.role, content: m.content }))); })
      .catch(() => {});
  }, [user]);

  // Auto-fetch analysis when panel opens on a finished game
  useEffect(() => {
    if (!gameOver || !currentPgn || analysis || analyzing) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const data = await analyzeGame(token, currentPgn, playerColor);
        if (data?.error) {
          setAnalyzeError(data.error);
        } else if (data?.summary) {
          setAnalysis(data);
        } else {
          setAnalyzeError("unavailable");
          setView("chat");
        }
      } catch {
        setAnalyzeError("unavailable");
        setView("chat");
      } finally {
        setAnalyzing(false);
      }
    })();
  }, [gameOver, currentPgn]); // eslint-disable-line

  // Auto-send hint when triggered externally
  useEffect(() => {
    if (!autoHint || didAutoHint.current || streaming) return;
    didAutoHint.current = true;
    setView("chat");
    sendMessage(autoHint);
  }, [autoHint]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, liveMessages]);

  async function sendMessage(overrideText) {
    const text = (overrideText || input).trim();
    if (!text || streaming) return;
    if (!overrideText) setInput("");
    setError(null);

    // Add user message + single assistant placeholder
    setMessages(prev => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    if (onHintUsed && overrideText) onHintUsed();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      console.log("BLANCHE FINAL TOKEN:", token ? token.slice(0, 20) + "…" : "MISSING");
      if (!token) {
        console.error("No session available");
        setMessages(prev => prev.slice(0, -2)); // remove both placeholders
        setError("Please refresh and sign in again.");
        return;
      }

      const res = await fetch(`${API_URL}/blanche/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, currentPgn }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages(prev => prev.slice(0, -2));
        if (err.error === "daily_limit_reached") {
          setError("Free limit reached for today. Upgrade to Arena Pass for more.");
        } else if (err.error === "monthly_limit_reached") {
          setError(`Monthly limit reached (${err.used}/${err.limit}). Upgrade to Pro for unlimited.`);
        } else if (err.error === "subscription_required") {
          setError("Upgrade to Arena Pass to continue chatting with Blanche.");
        } else {
          setError(err.error || "Something went wrong. Try again.");
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        console.log("STREAM RAW:", buf.slice(-100));

        // Split on double-newline (SSE event boundary), keep incomplete tail
        const parts = buf.split("\n\n");
        buf = parts.pop(); // last element may be incomplete

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            let payload;
            try {
              payload = JSON.parse(raw);
            } catch {
              continue;
            }
            console.log("STREAM PAYLOAD:", payload);

            if (payload.token) {
              setMessages(prev => {
                const next = [...prev];
                next[next.length - 1] = {
                  ...next[next.length - 1],
                  content: next[next.length - 1].content + payload.token,
                };
                return next;
              });
            }
            if (payload.done) {
              console.log("STREAM DONE");
              break outer;
            }
          }
        }
      }

      // Guard: replace empty assistant bubble
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && !last.content.trim()) {
          const next = [...prev];
          next[next.length - 1] = { ...last, content: "I couldn't generate a response. Try asking again." };
          return next;
        }
        return prev;
      });

    } catch {
      setMessages(prev => prev.slice(0, -2));
      setError("Connection error. Please try again.");
    } finally {
      setStreaming(false);
    }
  }

  const panelStyle = isMobile ? {
    position: "fixed", bottom: 0, left: 0, right: 0,
    height: "88vh", borderRadius: "20px 20px 0 0",
    background: DS.surface, borderTop: `1px solid ${DS.borderFaint}`,
    display: "flex", flexDirection: "column", zIndex: 300,
    boxShadow: "0 -12px 48px rgba(0,0,0,0.6)",
  } : {
    position: "fixed", left: 0, top: 56, bottom: 0, width: SIDEBAR_W,
    background: DS.surface, borderRight: `1px solid ${DS.borderFaint}`,
    display: "flex", flexDirection: "column", zIndex: 40,
    boxShadow: "4px 0 32px rgba(0,0,0,0.4)",
    animation: "slideUp 0.2s ease both",
  };

  const hasAnalysis = gameOver && currentPgn;

  return (
    <>
      {isMobile && (
        <div onClick={onClose} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          zIndex: 299, backdropFilter: "blur(2px)",
        }} />
      )}
      <div style={panelStyle}>

        {/* ── Header ── */}
        <div style={{
          padding: isMobile ? "20px 20px 12px" : "14px 18px",
          borderBottom: `1px solid ${DS.borderFaint}`,
          display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
          background: "linear-gradient(135deg, rgba(115,102,240,0.08), rgba(237,187,74,0.04))",
          borderRadius: isMobile ? "20px 20px 0 0" : 0,
          position: "relative",
        }}>
          {isMobile && (
            <div style={{
              position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
              width: 36, height: 4, borderRadius: 2, background: DS.borderMid,
            }} />
          )}
          <span style={{ fontSize: 20, color: DS.gold, filter: `drop-shadow(0 0 6px ${DS.goldGlow})` }}>♛</span>
          <div style={{ flex: 1 }}>
            <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", color: DS.text }}>
              Blanche
            </div>
            <div style={{ fontSize: 9, color: DS.textFaint, letterSpacing: "0.04em", marginTop: 1 }}>
              Chess Coach
            </div>
          </div>
          {/* View tabs */}
          {hasAnalysis && (
            <div style={{ display: "flex", background: DS.elevated, borderRadius: 6, padding: 2, gap: 2 }}>
              {["analysis", "chat"].map(v => (
                <button key={v} onClick={() => setView(v)} className="cinzel" style={{
                  padding: "4px 10px", borderRadius: 4, border: "none", cursor: "pointer",
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                  background: view === v ? DS.accent : "transparent",
                  color: view === v ? "#fff" : DS.textMuted,
                  transition: "all 0.12s",
                }}>
                  {v === "analysis" ? "ANALYSIS" : "CHAT"}
                </button>
              ))}
            </div>
          )}
          <button onClick={onClose} style={{
            background: "none", border: "none", color: DS.textMuted,
            cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 4, borderRadius: 4,
          }}>✕</button>
        </div>

        {/* ── Free tier indicator ── */}
        {isFree && !subLoading && (
          <div style={{
            padding: "6px 16px", flexShrink: 0,
            background: "rgba(115,102,240,0.06)",
            borderBottom: `1px solid rgba(115,102,240,0.12)`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 10, color: DS.textMuted }}>
              Free tier · limited access
            </span>
            <button onClick={() => onNavigate?.("settings")} className="cinzel" style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 9, color: DS.accent, letterSpacing: "0.04em", fontWeight: 700,
            }}>UPGRADE ↗</button>
          </div>
        )}

        {/* ── Analysis View ── */}
        {view === "analysis" && (
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {analyzing && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 32 }}>
                <span style={{ fontSize: 28, color: DS.gold, opacity: 0.5 }}>♛</span>
                <span className="cinzel" style={{ fontSize: 11, color: DS.textFaint, letterSpacing: "0.1em" }}>ANALYZING GAME…</span>
              </div>
            )}
            {analyzeError && analyzeError !== "unavailable" && !analyzing && (
              <div style={{ padding: 20, textAlign: "center", color: DS.danger, fontSize: 13 }}>{analyzeError}</div>
            )}
            {analysis && !analyzing && (
              <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Summary card */}
                <div style={{
                  background: "linear-gradient(135deg, rgba(237,187,74,0.09), rgba(115,102,240,0.07))",
                  border: `1px solid rgba(237,187,74,0.25)`,
                  borderRadius: 12, padding: "14px 16px",
                }}>
                  <div className="cinzel" style={{ fontSize: 9, color: DS.gold, letterSpacing: "0.14em", marginBottom: 6 }}>
                    SUMMARY
                  </div>
                  <div style={{ fontSize: 13, color: DS.text, lineHeight: 1.6 }}>{analysis.summary}</div>
                </div>

                {/* Insight cards */}
                {(analysis.insights || []).map((ins, i) => (
                  <InsightCard key={i} insight={ins} locked={isFree && i >= 2} onUpgrade={() => onNavigate?.("settings")} />
                ))}

                {/* Action row */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary cinzel" style={{ width: "100%", fontSize: 11, letterSpacing: "0.06em" }}
                    onClick={() => { onClose(); onStartBlancheGame?.(); }}>
                    ♛ Train with Blanche
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="cinzel"
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, cursor: isPro ? "pointer" : "default",
                        background: isPro ? "rgba(237,187,74,0.12)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${isPro ? "rgba(237,187,74,0.3)" : "rgba(255,255,255,0.08)"}`,
                        color: isPro ? DS.gold : DS.textFaint, fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.05em", fontFamily: "'Cinzel', serif",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                      }}
                      onClick={() => !isPro && onNavigate?.("settings")}
                    >
                      {!isPro && <span style={{ fontSize: 8, background: DS.gold, color: "#1A1200", borderRadius: 3, padding: "1px 4px", fontWeight: 700 }}>PRO</span>}
                      Deeper Analysis
                    </button>
                    <button className="btn btn-ghost btn-sm cinzel" style={{ flex: 1, fontSize: 10 }}
                      onClick={() => { onClose(); onNewGame?.(); }}>
                      ⟳ New Game
                    </button>
                  </div>
                </div>

                {/* Switch to chat */}
                <button onClick={() => setView("chat")} className="cinzel" style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 10, color: DS.textFaint, letterSpacing: "0.04em",
                  padding: "4px 0", textDecoration: "underline",
                }}>
                  Ask Blanche a question →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Chat View ── */}
        {view === "chat" && (
          <>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
              {analyzeError === "unavailable" && messages.length === 0 && (
                <div style={{
                  background: "rgba(115,102,240,0.07)", border: `1px solid rgba(115,102,240,0.18)`,
                  borderRadius: 10, padding: "12px 14px", fontSize: 12, color: DS.textMuted, lineHeight: 1.6,
                }}>
                  Analysis unavailable. Ask Blanche what went wrong.
                </div>
              )}
              {messages.length === 0 && liveMessages.length === 0 && analyzeError !== "unavailable" && (
                <EmptyState onStartBlancheGame={onStartBlancheGame} commentaryOn={commentaryOn} onToggleCommentary={onToggleCommentary} isBlancheGame={isBlancheGame} />
              )}
              {messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content}
                  isStreaming={streaming && i === messages.length - 1 && m.role === "assistant"} />
              ))}
              {liveMessages.map((text, i) => (
                <NarrationBubble key={`n-${i}`} text={text} />
              ))}
              {error && (
                <div style={{ fontSize: 12, color: DS.danger, padding: "10px 14px",
                  background: "rgba(224,70,70,0.08)", borderRadius: 8, border: `1px solid rgba(224,70,70,0.2)` }}>
                  {error}
                  {(error.includes("limit") || error.includes("Upgrade")) && (
                    <button onClick={() => onNavigate?.("settings")} className="cinzel" style={{
                      display: "block", marginTop: 8, background: DS.accent, border: "none",
                      borderRadius: 6, padding: "6px 14px", color: "#fff", fontSize: 10,
                      fontWeight: 700, letterSpacing: "0.06em", cursor: "pointer",
                    }}>Upgrade Now</button>
                  )}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Commentary toggle during active Blanche game */}
            {isBlancheGame && (
              <div style={{ padding: "6px 16px", borderTop: `1px solid ${DS.borderFaint}`, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                <button className="cinzel" onClick={onToggleCommentary} style={{
                  padding: "4px 14px", background: commentaryOn ? "rgba(237,187,74,0.1)" : "transparent",
                  border: `1px solid ${commentaryOn ? "rgba(237,187,74,0.28)" : "rgba(255,255,255,0.07)"}`,
                  borderRadius: 6, cursor: "pointer",
                  color: commentaryOn ? DS.gold : DS.textFaint,
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", transition: "all 0.15s",
                }}>
                  {commentaryOn ? "◉ Commentary On" : "○ Commentary Off"}
                </button>
              </div>
            )}

            {/* Input */}
            <div style={{ padding: "10px 14px", borderTop: `1px solid ${DS.borderFaint}`, flexShrink: 0 }}>
              {/* Free hint counter hint */}
              {isFree && !isBlancheGame && (
                <div style={{ fontSize: 10, color: DS.textFaint, marginBottom: 6, textAlign: "right" }}>
                  {Math.max(0, FREE_HINT_LIMIT - hintCount)} free {Math.max(0, FREE_HINT_LIMIT - hintCount) === 1 ? "message" : "messages"} left today
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Ask Blanche about this game…"
                  disabled={streaming}
                  rows={2}
                  style={{
                    flex: 1, background: DS.elevated, border: `1px solid ${DS.border}`,
                    borderRadius: 8, padding: "9px 12px", color: DS.text,
                    fontSize: 13, resize: "none", outline: "none", fontFamily: "inherit",
                    lineHeight: 1.5, opacity: streaming ? 0.6 : 1,
                  }}
                />
                <button onClick={() => sendMessage()} disabled={streaming || !input.trim()} style={{
                  background: DS.accent, border: "none", borderRadius: 8,
                  width: 38, height: 38, cursor: "pointer", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: (streaming || !input.trim()) ? 0.35 : 1,
                  color: "#fff", fontSize: 17, transition: "opacity 0.15s",
                }}>↑</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function InsightCard({ insight, locked, onUpgrade }) {
  const icons = {
    "Opening": "♟",
    "Turning Point": "⟳",
    "Biggest Mistake": "✕",
    "Missed Opportunity": "◎",
    "Best Move Missed": "◎",
    "What Went Well": "✓",
    "Key Weakness": "⚠",
    "Focus Next Game": "↑",
    "Improvement Tip": "↑",
    "Improvement": "↑",
  };
  return (
    <div style={{
      background: DS.elevated, border: `1px solid ${DS.borderFaint}`,
      borderRadius: 10, padding: "12px 14px",
      position: "relative", overflow: "hidden",
      filter: locked ? "blur(0px)" : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: DS.gold }}>{icons[insight.title] || "◆"}</span>
        <div className="cinzel" style={{ fontSize: 9, fontWeight: 700, color: DS.textMuted, letterSpacing: "0.1em" }}>
          {insight.title.toUpperCase()}
        </div>
        {insight.moveRef && (
          <code style={{
            marginLeft: "auto", fontSize: 10, color: DS.accent,
            background: "rgba(115,102,240,0.12)", borderRadius: 4, padding: "1px 6px",
          }}>{insight.moveRef}</code>
        )}
      </div>
      {locked ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 12, color: DS.textFaint, filter: "blur(4px)", lineHeight: 1.5, userSelect: "none" }}>
            {insight.explanation}
          </div>
          <button onClick={onUpgrade} className="cinzel" style={{
            background: "linear-gradient(135deg, rgba(237,187,74,0.18), rgba(237,187,74,0.08))",
            border: `1px solid rgba(237,187,74,0.3)`, borderRadius: 6,
            padding: "5px 12px", cursor: "pointer", color: DS.gold,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", alignSelf: "flex-start",
          }}>
            ✦ Unlock with Arena Pass
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: DS.textMuted, lineHeight: 1.6 }}>{insight.explanation}</div>
      )}
    </div>
  );
}

function EmptyState({ onStartBlancheGame, commentaryOn, onToggleCommentary, isBlancheGame }) {
  return (
    <div style={{ textAlign: "center", padding: "24px 8px" }}>
      <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.15, color: DS.gold }}>♛</div>
      <div className="cinzel" style={{ fontSize: 11, color: DS.textFaint, letterSpacing: "0.1em", marginBottom: 8 }}>
        Blanche — Chess Coach
      </div>
      <div style={{ fontSize: 12, color: DS.textMuted, lineHeight: 1.7, marginBottom: 20 }}>
        Ask about your last game, openings, tactics, or what to work on next.
      </div>
      {!isBlancheGame && (
        <div style={{
          background: "linear-gradient(135deg, rgba(237,187,74,0.07), rgba(115,102,240,0.05))",
          border: `1px solid rgba(237,187,74,0.18)`,
          borderRadius: 12, padding: "14px 12px", textAlign: "left",
        }}>
          <div className="cinzel" style={{ fontSize: 9, color: DS.gold, letterSpacing: "0.12em", marginBottom: 6 }}>
            TRAIN WITH BLANCHE
          </div>
          <div style={{ fontSize: 12, color: DS.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
            Play against Blanche and get real-time feedback on your decisions.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="cinzel" onClick={onStartBlancheGame} style={{
              flex: 1, padding: "8px 0",
              background: "linear-gradient(135deg, #FFD700, #C8950A)",
              border: "none", borderRadius: 8, cursor: "pointer",
              color: "#1A1200", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
            }}>
              ▶ Start Game
            </button>
            <button className="cinzel" onClick={onToggleCommentary} style={{
              padding: "8px 12px",
              background: commentaryOn ? "rgba(237,187,74,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${commentaryOn ? "rgba(237,187,74,0.3)" : "rgba(255,255,255,0.09)"}`,
              borderRadius: 8, cursor: "pointer",
              color: commentaryOn ? DS.gold : DS.textMuted,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", whiteSpace: "nowrap",
            }}>
              {commentaryOn ? "◉ Commentary" : "○ Commentary"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ role, content, isStreaming }) {
  const isBlanche = role === "assistant";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isBlanche ? "flex-start" : "flex-end", gap: 3 }}>
      {isBlanche && (
        <div className="cinzel" style={{ fontSize: 9, color: DS.gold, letterSpacing: "0.08em", paddingLeft: 2 }}>
          Blanche
        </div>
      )}
      <div style={{
        maxWidth: "92%",
        background: isBlanche ? DS.elevated : DS.accent,
        border: isBlanche ? `1px solid ${DS.borderFaint}` : "none",
        borderRadius: isBlanche ? "4px 12px 12px 12px" : "12px 4px 12px 12px",
        padding: "9px 13px",
        fontSize: 13, color: DS.text, lineHeight: 1.65,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
      }}>
        {content}
        {isStreaming && <span style={{ opacity: 0.4, marginLeft: 2 }}>▊</span>}
      </div>
    </div>
  );
}

function NarrationBubble({ text }) {
  return (
    <div style={{ textAlign: "center", padding: "2px 6px" }}>
      <div style={{
        display: "inline-block", fontSize: 11, fontStyle: "italic", color: DS.gold,
        opacity: 0.85, lineHeight: 1.55,
        background: "rgba(237,187,74,0.06)", border: "1px solid rgba(237,187,74,0.12)",
        borderRadius: 8, padding: "4px 12px",
      }}>
        ♛ {text}
      </div>
    </div>
  );
}
